import SwitchOwnerModal from "accountOwner/pages/Scheduler/components/Modals/SwitchOwnerModal";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import constants from "../../../constants";
import { getStorage, setStorage, sortedOwnerList } from "utils";
import {
  useAccountOwners,
  useUpdateAccountOwnerSelected,
} from "repositories/scheduler-repository";
import {
  initialSubscriptionPagesStateValues,
  subscriptionReturnValues,
} from "reducers/Subscription";
import { useGetAccountAccess } from "repositories/family-member-repository";
import { encodeId, decodeId } from "utils";
import {
  clearOfficeNavigationContext,
  getEffectiveOwnerId,
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
  OFFICE_NAV_CONTEXT_CHANGE_EVENT,
  resolveOwnerFromOwnerList,
} from "utils/officeNavigationContext";
const defaultAccessValues = {
  cancreateGroup: false,
  contactNumber: "",
  emailId: "",
  firstName: "",
  isActive: false,
  isAdmin: false,
  isDoctor: false,
  isMessenger: false,
  isPatientChat: false,
  isPatientVideoCall: false,
  isMessageTemplateModule: false,
  isReceptionist: false,
  lastName: "",
  profilePic: "",
  userId: 0,
  officeList: [],
  selectedOwnerIdString:"",
  getIdToCheckOwnerChange:()=>{},
};
const AccountOwnerContext = createContext({
  commonOwnerData: null,
  isStaff: false,
  ownerList: [],
  subscriptionValues: initialSubscriptionPagesStateValues,
  selectedId: 0,
  switchNewAccountOwner: () => {},
  isOwner: false,
  profile: null,
  ownerListLoading: false,
  subscriptionDetails: null,
  setIsAdminModalOpen: () => {},
  isAdmin: false,
  dataAccessValues: defaultAccessValues,
  getCommonAccessLoading: false,
  updateUserSelected:{},
  selectedOwnerIdString:"",
  getIdToCheckOwnerChange:()=>{},
  effectiveOwnerId: 0,
  effectiveOwnerIdString: "",
  effectiveOwnerData: null,
  isOfficeNavigationContext: false,
});
export default function AccountOwnerProvider({ children }) {
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [officeNavVersion, setOfficeNavVersion] = useState(0);
  const profile = useSelector((pre) => pre.userProfile?.profile);
  const subscriptionDetails = useSelector((prev) => prev.Subscription);
  const [commonSelectedOwner, setCommonSelectedOwner] = useState(null);
  const { isStaff, isOwner, isAdmin } = useMemo(() => {
    const isStaff = profile?.role?.systemRole === constants.systemRoles.staff;
    return {
      isStaff: isStaff,
      isOwner: profile?.role?.systemRole === constants.systemRoles.accountOwner,
      isAdmin: isStaff && profile?.isAdmin,
    };
  }, [profile?.role]);
  const { data: ownerList, isLoading: ownerListLoading } = useAccountOwners({
    enabled: isStaff,
  });
  const updateUserSelected = useUpdateAccountOwnerSelected();
  const selectedId = useMemo(
    () => (isOwner ? profile?.id : commonSelectedOwner?.id),
    [isOwner, profile, commonSelectedOwner]
  );

  const selectedOwnerIdString = useMemo(() => {
    if (!isStaff || selectedId == null || selectedId === "") {
      return "";
    }
    return `${constants.selectedOwnerId}=${encodeId(selectedId)}`;
  }, [isStaff, selectedId]);

  useEffect(() => {
    const handleOfficeNavContextChange = () =>
      setOfficeNavVersion((version) => version + 1);
    window.addEventListener(
      OFFICE_NAV_CONTEXT_CHANGE_EVENT,
      handleOfficeNavContextChange,
    );
    return () =>
      window.removeEventListener(
        OFFICE_NAV_CONTEXT_CHANGE_EVENT,
        handleOfficeNavContextChange,
      );
  }, []);

  const officeNavigationContext = useMemo(
    () => getOfficeNavigationContext(),
    [officeNavVersion],
  );
  const isOfficeNavigationContext = useMemo(
    () => isInOfficeNavigationContext(officeNavigationContext),
    [officeNavigationContext],
  );
  const effectiveOwnerId = useMemo(
    () => getEffectiveOwnerId(selectedId, officeNavigationContext),
    [selectedId, officeNavigationContext],
  );
  const effectiveOwnerIdString = useMemo(() => {
    if (!isStaff || effectiveOwnerId == null || effectiveOwnerId === "") {
      return "";
    }
    return `${constants.selectedOwnerId}=${encodeId(effectiveOwnerId)}`;
  }, [isStaff, effectiveOwnerId]);
  const effectiveOwnerData = useMemo(() => {
    if (
      isOfficeNavigationContext &&
      officeNavigationContext?.ownerId != null
    ) {
      const officeOwner = resolveOwnerFromOwnerList(
        ownerList,
        officeNavigationContext.ownerId,
      );
      if (officeOwner) return officeOwner;
      if (ownerListLoading) return null;
      return null;
    }
    return isStaff ? commonSelectedOwner : profile;
  }, [
    isOfficeNavigationContext,
    officeNavigationContext?.ownerId,
    ownerList,
    ownerListLoading,
    isStaff,
    commonSelectedOwner,
    profile,
  ]);

  const accessOwnerId = effectiveOwnerId;
  const { data: dataAccessArray, isLoading: getCommonAccessLoading } =
    useGetAccountAccess(
      { OwnerId: accessOwnerId },
      { enabled: !!accessOwnerId && isStaff }
    );
  const dataAccessValues = useMemo(() => {
    if (dataAccessArray?.length) {
      return dataAccessArray[0];
    }
    if (dataAccessArray?.userId) {
      return dataAccessArray;
    }
    return defaultAccessValues;
  }, [dataAccessArray]);

  const subscriptionValues = useMemo(
    () => subscriptionReturnValues(subscriptionDetails, effectiveOwnerId),
    [subscriptionDetails, effectiveOwnerId]
  );
  useEffect(() => {
    if (
      profile?.defaultOwner &&
      profile?.defaultOwner?.id &&
      ownerList &&
      ownerList?.length &&
      ownerList.some((owner) => owner?.id === profile?.defaultOwner?.id)
    ) {
      setCommonSelectedOwner(profile?.defaultOwner);
      return;
    }
    const preselectedOwner = getStorage(constants.cache.ownerKey);
    if (
      preselectedOwner &&
      ownerList &&
      ownerList?.length &&
      ownerList.some((owner) => owner?.id === preselectedOwner?.id)
    ) {
      setCommonSelectedOwner(preselectedOwner);
    } else if (ownerList && ownerList?.length) {
      setStorage(constants.cache.ownerKey, ownerList[0]);
      setCommonSelectedOwner(ownerList[0]);
    }
  }, [ownerList, profile?.id]);
  const switchNewAccountOwner = (ownerData, check) => {
    if (ownerData?.id) {
      setCommonSelectedOwner(ownerData);
      const stringify = JSON.stringify(ownerData);
      setStorage(constants.cache.ownerKey, stringify, true);
      clearOfficeNavigationContext();
      if (check) return;
      updateUserSelected.mutate({ ownerId: ownerData.id });
    }
  };
const getIdToCheckOwnerChange=()=>{
  if(!isStaff) return false;
  const locationParams=new URLSearchParams(window.location.search);
  const ownerId=locationParams.get(constants.selectedOwnerId);
  if(ownerId&&effectiveOwnerId && +decodeId(ownerId)!==+effectiveOwnerId){
    return true;
  }
  return false;
}

  const values = {
    commonOwnerData: isStaff ? commonSelectedOwner : profile,
    isStaff,
    ownerList: sortedOwnerList(ownerList || []),
    subscriptionValues,
    selectedId,
    isOwner,
    profile,
    switchNewAccountOwner,
    ownerListLoading,
    subscriptionDetails,
    setIsAdminModalOpen,
    isAdmin,
    dataAccessValues,
    getCommonAccessLoading,
    selectedOwnerIdString,
    getIdToCheckOwnerChange,
    effectiveOwnerId,
    effectiveOwnerIdString,
    effectiveOwnerData,
    isOfficeNavigationContext,
    officeNavigationContext,
  };

  return (
    <AccountOwnerContext.Provider value={values}>
      {children}
      {isStaff && isAdminModalOpen && (
        <SwitchOwnerModal
          subTitle={""}
          showSwitchOwnerModal={isAdminModalOpen}
          setShowSwitchOwnerModal={setIsAdminModalOpen}
          ownerList={values.ownerList}
          selectedOwner={commonSelectedOwner}
          setSelectedOwner={switchNewAccountOwner}
        />
      )}
    </AccountOwnerContext.Provider>
  );
}
export const useAccountOwnerCommon = () => {
  const context = useContext(AccountOwnerContext);
  return context;
};
